import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';
import CpfCnpj from '@react-br-forms/cpf-cnpj-mask';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import * as Yup from 'yup';

import api from '~/services/api';
import toast from '~/services/toast';
import { API_NODE_URL, APP_ERROR, APP_SETTINGS } from '~/constants';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import { somenteDigitos, validaCpfCnpj } from '~/utils/documento';

import { TextMaskCustom } from '../ModalIdentificarCliente';

const ModalEditarCliente = () => {
  const temaAtual = JSON.parse(sessionStorage.getItem(APP_SETTINGS));
  const dispatch = useDispatch();

  const { venda, showModalEditarCliente } = useSelector(state => state.pdv);

  const [loading, setLoading] = useState(false);

  const clienteId = venda?.intCliente?.id;
  const exigeCpf = Boolean(showModalEditarCliente?.exigeCpf);

  const validationSchema = Yup.object().shape({
    clienteNome: Yup.string().required('Nome do cliente é obrigatório'),
    clienteCpfCnpj: exigeCpf
      ? Yup.string().required('CPF é obrigatório para esta operação')
      : Yup.string().nullable()
  });

  const labelProps = {
    shrink: true,
    style: { color: temaAtual?.theme === 'DEFAULT' ? '#000' : '#FFF', fontWeight: 'bold' }
  };

  const handleClose = () => {
    dispatch(pdvActions.setShowModalEditarCliente({ show: false }));
  };

  const handleSubmit = async values => {
    const clienteNome = values.clienteNome.toUpperCase().replace(/\s+$/, '');
    const clienteCpfCnpj = values.clienteCpfCnpj || null;
    const clienteTelefone = values.clienteTelefone ?? '';
    const clienteCelular = values.clienteCelular ?? '';

    if (clienteCpfCnpj && !validaCpfCnpj(clienteCpfCnpj)) {
      return toast.warning('CPF ou CNPJ inválido!');
    }

    setLoading(true);
    try {
      const natureza = clienteCpfCnpj ? (somenteDigitos(clienteCpfCnpj).length === 11 ? 'F' : 'J') : null;
      const pfCpf = natureza === 'F' ? clienteCpfCnpj : null;
      const pjCnpj = natureza === 'J' ? clienteCpfCnpj : null;

      if (clienteId) {
        await api.put(`${API_NODE_URL}/integracao/cliente/${clienteId}/identificacao`, {
          razaoSocial: clienteNome,
          telefone: clienteTelefone,
          celular: clienteCelular,
          pfCpf,
          pjCnpj
        });
        toast.success('Cadastro do cliente atualizado!');
      }

      dispatch(
        pdvActions.atualizarDadosCliente({
          clienteNome,
          clienteCpfCnpj,
          clienteTelefone,
          clienteCelular,
          intCliente: {
            razaoSocial: clienteNome,
            telefone: clienteTelefone,
            celular: clienteCelular,
            ...(natureza ? { natureza } : {}),
            pfCpf,
            pjCnpj
          }
        })
      );
      handleClose();
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Mui.Dialog
      open={Boolean(showModalEditarCliente?.show)}
      onKeyDown={key => {
        if (key.code === 'Escape') handleClose();
      }}
      PaperComponent={Mui.Paper}
      maxWidth={'md'}
      style={{ zIndex: '999' }}
    >
      <Mui.DialogTitle>
        <Mui.AppBar color="primary" position="static">
          <Mui.Toolbar>
            <Mui.Typography variant="h5" style={{ textTransform: 'upperCase' }}>
              Editar cliente
            </Mui.Typography>
            <Mui.Box flexGrow={1} />
            <Mui.IconButton color="inherit" onClick={handleClose} aria-label="close">
              <MuiIcons.Close />
            </Mui.IconButton>
          </Mui.Toolbar>
        </Mui.AppBar>
      </Mui.DialogTitle>

      <Formik
        initialValues={{
          clienteNome: venda?.clienteNome ?? '',
          clienteCpfCnpj: venda?.clienteCpfCnpj ?? venda?.intCliente?.pfCpf ?? venda?.intCliente?.pjCnpj ?? '',
          clienteTelefone: venda?.clienteTelefone ?? '',
          clienteCelular: venda?.clienteCelular ?? ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Mui.DialogContent dividers style={{ padding: 32, width: 700 }}>
              {exigeCpf && (
                <Mui.Box mb={2}>
                  <Mui.Typography variant="body2" color="error">
                    Informe o CPF do cliente para gravar este documento.
                  </Mui.Typography>
                </Mui.Box>
              )}
              <Mui.Grid container spacing={4}>
                <Mui.Grid item md={4} style={{ padding: 8 }}>
                  <Mui.TextField
                    name="clienteCpfCnpj"
                    label={'CPF/CNPJ'}
                    value={values.clienteCpfCnpj}
                    error={Boolean(touched.clienteCpfCnpj && errors.clienteCpfCnpj)}
                    helperText={touched.clienteCpfCnpj && errors.clienteCpfCnpj}
                    autoFocus
                    fullWidth
                    onBlur={handleBlur}
                    onChange={e => setFieldValue('clienteCpfCnpj', e.target.value)}
                    variant="outlined"
                    InputProps={{ inputComponent: CpfCnpj }}
                    InputLabelProps={labelProps}
                  />
                </Mui.Grid>
                <Mui.Grid item md={4} style={{ padding: 8 }}>
                  <Mui.TextField
                    name="clienteTelefone"
                    label={'Telefone'}
                    value={values.clienteTelefone}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{ inputComponent: TextMaskCustom }}
                    InputLabelProps={labelProps}
                  />
                </Mui.Grid>
                <Mui.Grid item md={4} style={{ padding: 8 }}>
                  <Mui.TextField
                    name="clienteCelular"
                    label={'Celular'}
                    value={values.clienteCelular}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{ inputComponent: TextMaskCustom }}
                    InputLabelProps={labelProps}
                  />
                </Mui.Grid>
                <Mui.Grid item md={12}>
                  <Mui.TextField
                    name="clienteNome"
                    label={'Nome'}
                    value={values.clienteNome?.toUpperCase()}
                    error={Boolean(touched.clienteNome && errors.clienteNome)}
                    helperText={touched.clienteNome && errors.clienteNome}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    InputLabelProps={labelProps}
                    inputProps={{ maxLength: 50 }}
                  />
                </Mui.Grid>
              </Mui.Grid>
            </Mui.DialogContent>
            <Mui.DialogActions>
              <Mui.Box flex={1} display="flex" justifyContent="flex-end" p={1}>
                <Mui.Button
                  type="submit"
                  variant="contained"
                  size="large"
                  color="primary"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <Mui.CircularProgress size={20} color="inherit" />
                    ) : (
                      <MuiIcons.CheckCircle />
                    )
                  }
                >
                  <span style={{ paddingLeft: 8 }}>{loading ? 'Processando...' : 'Salvar'}</span>
                </Mui.Button>
                <Mui.Button
                  startIcon={<MuiIcons.Close />}
                  size="large"
                  onClick={handleClose}
                  style={{ marginLeft: 16 }}
                >
                  CANCELAR (ESC)
                </Mui.Button>
              </Mui.Box>
            </Mui.DialogActions>
          </form>
        )}
      </Formik>
    </Mui.Dialog>
  );
};

export default ModalEditarCliente;
